import type { InsightChartsBlockProps } from '@components/layout/main/insightCharts/InsightChartsBlock';
import type { Metric } from '@enums';
import { isValidElement, type ReactNode } from 'react';
import type { ChartType } from 'types/common';

export const isInlineChart = (url: URL, linkLabel: string): boolean => {
  const insightIds = url.searchParams.get('insights');
  const isPieInline = linkLabel.includes('$pie-chart');
  const isHorBarInline = linkLabel.includes('$hor-bar-chart');
  const isStackedBarInline = linkLabel.includes('$stacked-bar-chart');

  return (isPieInline || isHorBarInline || isStackedBarInline) && !!insightIds;
};

/**
 * Maps a URL to InsightChartsBlockProps
 * @param url - The URL to map to InsightChartsBlockProps
 * @param linkLabel - The link label to get the chart type from
 * @returns InsightChartsBlockProps
 */
export const mapUrlToInsightChartsBlockProps = (
  url: URL,
  linkLabel: string
): InsightChartsBlockProps => {
  const insightIds = url.searchParams.get('insights')?.split(',');

  return {
    insightIds,
    type: getChartType(linkLabel),
    metric: url.searchParams.get('metric') as Metric,
    layout:
      (url.searchParams.get('layout') as 'horizontal' | 'vertical') ??
      'horizontal'
  };
};

export const getChartType = (linkLabel: string): ChartType => {
  const isPieInline = linkLabel.includes('$pie-chart');
  const isHorBarInline = linkLabel.includes('$hor-bar-chart');
  const isStackedBarInline = linkLabel.includes('$stacked-bar-chart');

  return isPieInline
    ? 'pie'
    : isHorBarInline
      ? 'bar'
      : isStackedBarInline
        ? 'stacked-bar'
        : undefined;
};

export function flattenAgentLinkChildren(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(flattenAgentLinkChildren).join('');
  }

  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode };

    if (props.children != null) {
      return flattenAgentLinkChildren(props.children);
    }
  }

  return '';
}
